/* eslint-disable @typescript-eslint/consistent-type-assertions */

import { and, assertEvent, not, raise, setup } from 'xstate5';

import { LockOrderReason } from '@sg/graphql-schema';

import { OrderingActions } from './ordering-machine.actions';
import { OrderingActors } from './ordering-machine.actors';
import { initialContext, type OrderingContext } from './ordering-machine.context';
import { OrderingDelays } from './ordering-machine.delays';
import { type OrderingEvent } from './ordering-machine.events';
import { OrderingGuards } from './ordering-machine.guards';
import * as Inputs from './ordering-machine.input';

// eslint-disable-next-line no-unused-vars -- Required for inferred XState declarations.
import type { GuardPredicate } from 'xstate5/guards';

export const orderingMachine = setup({
  types: {
    context: {} as OrderingContext,
    events: {} as OrderingEvent,
    input: {} as Partial<OrderingContext>,
  },
  actions: OrderingActions,
  actors: OrderingActors,
  guards: OrderingGuards,
  delays: OrderingDelays,
}).createMachine({
  context: ({ input }) => ({ ...initialContext, ...input }),
  type: 'parallel',
  on: {
    START: {
      target: ['#fetching-initial-data', '#preparing-to-fetch-rewards'],
    },
    CHANGE_PAYMENT_METHOD: {
      actions: ['changePaymentMethod'],
    },
    SET_CONFIRM_STRIPE: {
      actions: ['setConfirmStripe'],
    },
    CANCEL_REQUEST: '#idle',
    START_STRIPE_CONFIRMATION: {
      target: '#confirming-stripe',
      actions: ['setHasTriggeredOrderSubmission'],
    },
    STRIPE_FAILED_TO_LOAD: {
      actions: ['setStripeFailedToLoad'],
    },
    SET_STRIPE_FAILURE_AS_ALREADY_SHOWN: {
      actions: ['setStripeFailureAsAlreadyShown'],
    },
    SHOW_ORDER_PLAYBACK: {
      target: '#order-playback',
      actions: ['changePaymentMethod', 'setHasTriggeredOrderSubmission'],
    },
    HAS_STARTED_POLLING: {
      actions: ['clearHasTriggeredOrderSubmission'],
    },
    INCREMENT_PURCHASE_ATTEMPT: {
      actions: ['incrementPurchaseAttempt'],
    },
    PAYMENT_FULFILLED: '#fetching-state-post-order',
    PAYMENT_CANCELED: '#checkout-ledger',
    TIMESLOT_UNAVAILABLE: [
      {
        target: '#post-order-failure-with-next-day-order-notice',
        actions: ['handleTimeslotUnavailable', 'incrementPurchaseAttempt'],
        guard: 'checkIfNextTimeslotIsTomorrow',
      },
      {
        target: '#timeslot-adjusted-notice',
        actions: ['handleTimeslotUnavailable', 'incrementPurchaseAttempt'],
      },
    ],
    SUBMIT_ORDER: {
      target: '#submitting-order',
      actions: ['changePaymentMethod', 'setHasTriggeredOrderSubmission'],
    },
  },
  states: {
    ordering: {
      initial: 'idle',
      states: {
        idle: {
          initial: 'idle',
          states: {
            idle: {
              id: 'idle',
              on: {
                FETCH_INITIAL_DATA: {
                  target: '#fetching-initial-data',
                },
                ADD_UPSELL_TO_BAG: {
                  target: '#adding-upsell-to-bag',
                  actions: ['setUpsellBeingAdded'],
                },
                START_CHANGING_LINE_ITEM_QUANTITY: {
                  target: '#line-items-waiting',
                  actions: ['setLineItemIdBeingUpdated'],
                },
                STOP_CHANGING_LINE_ITEM_QUANTITY: {
                  actions: ['removeLineItemIdBeingUpdated'],
                },
                CHANGE_LINE_ITEM_QUANTITY: {
                  target: '#changing-line-item-quantity',
                },
                CHANGE_UTENSILS_USAGE: {
                  actions: ['changeUtensilsUsage'],
                },
                CHANGE_DROPOFF_LOCATION: {
                  actions: ['changeDropoffLocation'],
                },
                SHOW_LOYALTY_INFO: {
                  target: '#loyalty-info',
                },
                SHOW_LOYALTY_TERMS: {
                  target: '#loyalty-terms',
                  actions: ['setRewardTermsBeingShown'],
                },
                APPLY_REWARD: {
                  target: '#applying-reward',
                  actions: ['setRewardIdBeingUpdated'],
                },
                REMOVE_REWARD: {
                  target: '#removing-reward',
                  actions: ['setRewardIdBeingUpdated'],
                },
                APPLY_PROMO_CODE: {
                  target: '#applying-promo-code',
                },
                REQUEST_CHANNEL_LOCATION_CHANGE: {
                  actions: ['changeChannelOrLocation'],
                },
                REQUEST_TIME_CHANGE: [
                  { target: '#changing-time', guard: 'checkIfLoggedIn' }, // prettier-ignore
                  { actions: ['requestLogin'] },
                ],
                REQUEST_DELIVERY_PREFERENCES_CHANGE: {
                  target: '#changing-delivery-preferences',
                },
                REQUEST_CHECKOUT_LEDGER: [
                  {
                    target: '#upsell-interstitial',
                    guard: and([
                      'checkIfModalCohort',
                      'checkIfEligibleForUpsellExperiment',
                      'checkIfUpsellModalNotShown',
                    ]),
                  },
                  { guard: 'checkIfGroupOrderNeedsLock' },
                  {
                    target: '#checkout-ledger',
                  },
                ],
                PROCEED_TO_CHECKOUT: '#opening-checkout-ledger',
              },
            },
            postOrder: {
              id: 'post-order',
              entry: ['storeOrderPostSubmission'],
              after: {
                postOrderDelay: {
                  actions: [
                    'handlePostOrderPurchaseModal',
                    'handlePostOrderNotifications',
                    'handlePostOrderTracking',
                    'clearSharedLineItemAttribution',
                    'handlePostOrderHistory',
                    'handlePostOrderAppReview',
                    'navigatePostOrder',
                    'clearLocalStorageOrderId',
                    'resetContext',
                  ],
                },
              },
            },
            changingTime: {
              id: 'changing-time',
              on: {
                CHANGE_TIME: [
                  {
                    target: '#idle',
                    actions: ['changeTime', 'storeWantedTime'],
                    guard: 'checkIfDoesNotNeedsToShowFutureDateModal',
                  },
                  {
                    target: '#showing-future-date-warning',
                    actions: ['changeTime', 'storeWantedTime'],
                    guard: 'checkIfNeedsToShowFutureDateModal',
                  },
                ],
              },
            },
            changingDeliveryPreferences: {
              id: 'changing-delivery-preferences',
              on: {
                CHANGE_DELIVERY_PREFERENCES: {
                  target: '#idle',
                  actions: ['changeDeliveryPreferences'],
                },
              },
            },
            showingFutureDateWarning: {
              id: 'showing-future-date-warning',
            },
            showingRewardWarning: {
              id: 'showing-reward-warning',
            },
            checkoutLedger: {
              id: 'checkout-ledger',
              on: {
                CHANGE_TIP: { actions: ['changeTip'] },
                REQUEST_PAYMENT_METHOD_CHANGE: '#changing-payment-method',
                CHANGE_CREDIT_USAGE: { actions: ['changeCreditUsage'] },
              },
            },
            upsellInterstitial: {
              id: 'upsell-interstitial',
              initial: 'idle',
              entry: ['setHasShownUpsellModal'],
              on: {
                REQUEST_CHECKOUT_LEDGER: [
                  { target: '#idle', guard: 'checkIfGroupOrderNeedsLock' },
                  { target: '#opening-checkout-ledger' },
                ],
                START_CHANGING_LINE_ITEM_QUANTITY: {
                  actions: ['setLineItemIdBeingUpdated'],
                },
                STOP_CHANGING_LINE_ITEM_QUANTITY: {
                  actions: ['removeLineItemIdBeingUpdated'],
                },
              },
              states: {
                idle: {
                  id: 'upsell-interstitial-idle',
                  on: {
                    ADD_UPSELL_TO_BAG: {
                      target: 'addingUpsell',
                      actions: ['setUpsellBeingAdded'],
                    },
                  },
                },
                addingUpsell: {
                  on: {
                    // NOTE: The stepper timeout can fire a late stop event after add starts.
                    //       This keeps the active upsell ID until the add actor resolves.
                    STOP_CHANGING_LINE_ITEM_QUANTITY: {
                      actions: [],
                    },
                  },
                  invoke: {
                    id: 'addUpsellToBag',
                    src: 'addUpsellToBag',
                    input: Inputs.getAddUpsellToBagInput,
                    onDone: {
                      target: 'postAddUpsell',
                      actions: {
                        type: 'onAddUpsellToBagDone',
                        params: ({ event }) => ({
                          cart: 'cart' in event.output ? event.output.cart : undefined,
                        }),
                      },
                    },
                    onError: {
                      target: 'idle',
                      actions: ['clearLineItemIdsBeingUpdated'],
                    },
                  },
                },
                postAddUpsell: {
                  always: [
                    {
                      target: ['#idle', '#fetching-rewards'],
                      guard: and(['checkIfNoAvailableUpsellItems', 'checkIfLoggedIn']),
                    },
                    {
                      target: '#idle',
                      guard: 'checkIfNoAvailableUpsellItems',
                    },
                    {
                      target: ['#upsell-interstitial-idle', '#fetching-rewards'],
                      guard: 'checkIfLoggedIn',
                    },
                    {
                      target: '#upsell-interstitial-idle',
                    },
                  ],
                },
              },
            },
            openingCheckoutLedger: {
              id: 'opening-checkout-ledger',
              description:
                'Delays opening the ledger to avoid a modal stacking issue that causes the ledger to open already scrolled down.',
              after: { 100: '#checkout-ledger' },
            },
            postOrderFailureWithNextDayOrderNotice: {
              id: 'post-order-failure-with-next-day-order-notice',
              after: { 100: '#next-day-order-notice' },
            },
            timeslotAdjustedNotice: {
              id: 'timeslot-adjusted-notice',
              on: {
                REQUEST_TIME_CHANGE: '#changing-time',
                REQUEST_CHECKOUT_LEDGER: '#opening-checkout-ledger',
              },
            },
            nextDayOrderNotice: { id: 'next-day-order-notice' },
            orderPlayback: {
              id: 'order-playback',
              on: {
                CANCEL_REQUEST: {
                  target: '#idle',
                  actions: ['trackOrderPlaybackGoBack'],
                },
              },
              after: {
                orderSubmissionDelay: [
                  {
                    target: '#confirming-stripe',
                    actions: ['trackOrderPlaybackAutoSubmit', 'confirmStripe'],
                  },
                ],
              },
            },
            changingPaymentMethod: {
              id: 'changing-payment-method',
              on: {
                REQUEST_CHECKOUT_LEDGER: '#checkout-ledger',
                REQUEST_GIFT_CARD_FORM: '#gift-card-form',
                FINISH_PAYMENT_SELECTION: '#checkout-ledger',
                CHANGE_PAYMENT_METHOD: {
                  actions: ['changePaymentMethod'],
                },
              },
            },
            giftCardForm: {
              id: 'gift-card-form',
              on: {
                REQUEST_PAYMENT_METHOD_CHANGE: '#changing-payment-method',
                APPLY_GIFT_CARD: '#applying-gift-card',
              },
            },
            loyaltyInfo: { id: 'loyalty-info' },
            loyaltyTerms: { id: 'loyalty-terms' },
          },
        },
        loading: {
          initial: 'waiting',
          states: {
            waiting: {},
            fetchingInitialData: {
              id: 'fetching-initial-data',
              invoke: {
                id: 'fetchInitialData',
                src: 'fetchInitialData',
                onDone: {
                  target: '#retrieving-initial-wanted-time',
                  actions: [
                    {
                      type: 'onFetchInitialDataDone',
                      params: ({ event }) => event.output,
                    },
                    'trackUpsellExperimentStartedIfEligible',
                  ],
                },
              },
            },
            retrievingInitialWantedTime: {
              id: 'retrieving-initial-wanted-time',
              invoke: {
                id: 'getInitialWantedTime',
                src: 'getInitialWantedTime',
                input: ({ context }) => ({
                  orderId: context.cart.orderId ?? '',
                  wantedTimes: context.cart.wantedTimes,
                }),
                onDone: [
                  {
                    target: '#idle',
                    actions: ['onGetInitialWantedTimeDone', 'startPollingPaymentState'],
                    guard: 'checkIfShouldFetchInitialPaymentState',
                  },
                  {
                    target: '#idle',
                    actions: ['onGetInitialWantedTimeDone'],
                  },
                ],
                onError: '#idle',
              },
            },
            fetchingStatePostOrder: {
              id: 'fetching-state-post-order',
              invoke: {
                id: 'fetchStatePostOrder',
                src: 'fetchStatePostOrder',
                input: ({ context }) => ({
                  orderId: context.orderId || context.cart.orderId,
                }),
                onDone: {
                  target: '#post-order',
                  actions: ['onFetchStatePostOrderDone'],
                },
                onError: '#post-order',
              },
            },
            addingUpsellToBag: {
              id: 'adding-upsell-to-bag',
              invoke: {
                id: 'addUpsellToBag',
                src: 'addUpsellToBag',
                input: Inputs.getAddUpsellToBagInput,
                onDone: [
                  {
                    target: ['#idle', '#fetching-rewards'],
                    actions: {
                      type: 'onAddUpsellToBagDone',
                      params: ({ event }) => ({
                        cart: 'cart' in event.output ? event.output.cart : undefined,
                      }),
                    },
                    guard: 'checkIfLoggedIn',
                  },
                  {
                    target: '#idle',
                    actions: {
                      type: 'onAddUpsellToBagDone',
                      params: ({ event }) => ({
                        cart: 'cart' in event.output ? event.output.cart : undefined,
                      }),
                    },
                  },
                ],
                onError: {
                  target: '#idle',
                  actions: ['clearLineItemIdsBeingUpdated'],
                },
              },
            },
            changingLineItemQuantity: {
              id: 'changing-line-item-quantity',
              invoke: {
                id: 'updateLineItem',
                src: 'updateLineItem',
                input: ({ context, event }) => {
                  assertEvent(event, 'CHANGE_LINE_ITEM_QUANTITY');
                  return {
                    lineItemId: event.params.lineItemId,
                    productId: event.params.productId,
                    quantity: event.params.quantity,
                    resetQuantity: event.params.resetQuantity,
                    lineItems: context.cart.lineItems,
                  };
                },
                onDone: [
                  {
                    target: ['#idle', '#fetching-rewards'],
                    actions: ['onUpdateLineItemDone'],
                    guard: 'checkIfLoggedIn',
                  },
                  {
                    target: '#idle',
                    actions: ['onUpdateLineItemDone'],
                  },
                ],
                onError: {
                  target: '#idle',
                  actions: ['clearLineItemIdsBeingUpdated'],
                },
              },
            },
            applyingReward: {
              id: 'applying-reward',
              invoke: {
                id: 'applyReward',
                src: 'applyReward',
                input: ({ context, event }) => {
                  assertEvent(event, 'APPLY_REWARD');
                  return {
                    orderId: (context.orderId || context.cart.orderId) ?? '',
                    rewardId: event.params.rewardId,
                  };
                },
                onDone: [
                  {
                    target: ['#idle', '#fetching-rewards'],
                    actions: ['onApplyRewardDone'],
                    guard: 'checkIfRewardApplied',
                  },
                  {
                    target: '#showing-reward-warning',
                    actions: ['onRewardError'],
                    guard: 'checkIfRewardNotApplied',
                  },
                ],
              },
            },
            removingReward: {
              id: 'removing-reward',
              invoke: {
                id: 'removeReward',
                src: 'removeReward',
                input: ({ context, event }) => {
                  assertEvent(event, 'REMOVE_REWARD');
                  return {
                    orderId: (context.orderId || context.cart.orderId) ?? '',
                  };
                },
                onDone: [
                  {
                    target: ['#idle', '#fetching-rewards'],
                    actions: ['onRemoveRewardDone'],
                    guard: 'checkIfRewardRemoved',
                  },
                  {
                    target: '#showing-reward-warning',
                    actions: ['onRewardError'],
                    guard: 'checkIfRewardNotRemoved',
                  },
                ],
              },
            },
            applyingPromoCode: {
              id: 'applying-promo-code',
              invoke: {
                id: 'applyPromoCode',
                src: 'applyPromoCode',
                input: ({ event }) => {
                  assertEvent(event, 'APPLY_PROMO_CODE');
                  return { code: event.params.code };
                },
                onDone: [
                  {
                    target: '#idle',
                    actions: ['onApplyPromoCodeDone'],
                    guard: 'checkIfPromoCodeApplied',
                  },
                  {
                    target: '#showing-reward-warning',
                    actions: ['onRewardError'],
                  },
                ],
              },
            },
            applyingGiftCard: {
              id: 'applying-gift-card',
              invoke: {
                id: 'applyGiftCard',
                src: 'applyGiftCard',
                input: ({ event }) => {
                  assertEvent(event, 'APPLY_GIFT_CARD');
                  return {
                    code: event.params.code,
                    regCode: event.params.regCode,
                    awsWafToken: event.params.awsWafToken,
                  };
                },
                onDone: [
                  {
                    target: '#changing-payment-method',
                    actions: ['onApplyGiftCardDone'],
                    guard: 'checkIfGiftCardApplied',
                  },
                  {
                    target: '#gift-card-form',
                    guard: 'checkIfGiftCardNotApplied',
                  },
                ],
              },
            },
            confirmingStripe: {
              id: 'confirming-stripe',
            },
            submittingOrder: {
              id: 'submitting-order',
              invoke: {
                id: 'submitOrder',
                src: 'submitOrder',
                input: ({ context, event }) => {
                  const paymentMethodOrIntentId =
                    event.type === 'SUBMIT_ORDER'
                      ? event.params?.paymentMethodId || context.paymentMethodOrIntentId // prettier-ignore
                      : context.paymentMethodOrIntentId;

                  return {
                    paymentMethodOrIntentId,
                    cart: context.cart,
                    dropoffLocationId: context.dropoffLocation,
                    wantedTime: context.wantedTime,
                    selectedTip: context.selectedTip ?? 0,
                    shouldUseCredit: context.shouldUseCredit ?? false,
                    shouldIncludeUtensils: context.shouldIncludeUtensils ?? false, // prettier-ignore
                  };
                },
                onError: {
                  target: '#idle',
                  actions: ['incrementPurchaseAttempt'],
                },
                onDone: [
                  {
                    target: '#post-order',
                    guard: 'checkIfOrderSubmissionSuccess',
                  },
                  {
                    target: '#post-order-failure-with-next-day-order-notice',
                    actions: ['handleTimeslotUnavailable', 'incrementPurchaseAttempt'],
                    guard: 'checkIfWillBeFutureOrder',
                  },
                  {
                    target: '#timeslot-adjusted-notice',
                    actions: ['handleTimeslotUnavailable', 'incrementPurchaseAttempt'],
                    guard: 'checkIfTimeslotIsUnavailable',
                  },
                  {
                    target: '#idle',
                    actions: ['incrementPurchaseAttempt'],
                    guard: 'checkIfGenericOrderSubmissionError',
                  },
                ],
              },
            },
          },
        },
      },
    },
    'group-ordering': {
      initial: 'idle',
      states: {
        idle: {
          on: {
            REQUEST_GROUP_ORDER_CONVERSION: 'confirming-conversion',
            REQUEST_GROUP_ORDER_SHARE: 'sharing',
            REQUEST_GROUP_ORDER_LEAVE: 'confirming-leave',
            REQUEST_CHECKOUT_LEDGER: {
              target: 'confirming-lock',
              guard: and([
                'checkIfGroupOrderNeedsLock',
                not(
                  and([
                    'checkIfModalCohort',
                    'checkIfEligibleForUpsellExperiment',
                    'checkIfUpsellModalNotShown',
                  ]),
                ),
              ]),
              actions: {
                type: 'setGroupOrderLockReason',
                params: { reason: LockOrderReason.Checkout },
              },
            },
          },
        },
        'confirming-lock': {
          on: {
            CANCEL_GROUP_ORDER_LOCK_REQUEST: {
              target: 'idle',
              actions: ['clearGroupOrderLockReason'],
            },
            CONFIRM_GROUP_ORDER_LOCK: 'locking',
          },
        },
        locking: {
          invoke: {
            id: 'lockGroupOrder',
            src: 'lockGroupOrder',
            input: Inputs.getLockGroupOrderInput,
            onDone: {
              target: 'idle',
              actions: [
                'setHasLockedGroupOrder',
                'clearGroupOrderLockReason',
                raise({ type: 'PROCEED_TO_CHECKOUT' }),
              ],
            },
            onError: {
              target: 'confirming-lock',
              actions: {
                type: 'handleLockGroupOrderError',
                params: ({ event }) => ({
                  errorMessage: event.error instanceof Error ? event.error.message : undefined,
                }),
              },
            },
          },
        },
        'confirming-conversion': {
          on: {
            CANCEL_GROUP_ORDER_CONVERSION_REQUEST: 'idle',
            CONVERT_TO_GROUP_ORDER: 'converting',
          },
        },
        converting: {
          invoke: {
            id: 'convertToGroupOrder',
            src: 'convertToGroupOrder',
            input: ({ event }) => {
              assertEvent(event, 'CONVERT_TO_GROUP_ORDER');

              return event.params;
            },
            onDone: {
              target: 'sharing',
              actions: {
                type: 'updateCartFromPolling',
                params: ({ event }) => event.output,
              },
            },
            onError: {
              target: 'confirming-conversion',
              actions: 'handleConvertToGroupOrderError',
            },
          },
        },
        sharing: {
          on: {
            CLOSE_GROUP_ORDER_SHARE: 'idle',
          },
        },
        'confirming-leave': {
          on: {
            CANCEL_GROUP_ORDER_LEAVE_REQUEST: 'idle',
            LEAVE_GROUP_ORDER: 'leaving',
          },
        },
        leaving: {
          invoke: {
            id: 'leaveGroupOrder',
            src: 'leaveGroupOrder',
            onDone: [
              {
                target: 'idle',
                actions: ['handleLeaveGroupOrderSuccess'],
                guard: {
                  type: 'checkIfGroupOrderLeft',
                  params: ({ event }) => event.output,
                },
              },
              {
                target: 'idle',
              },
            ],
            onError: {
              target: 'confirming-leave',
              actions: {
                type: 'handleLeaveGroupOrderError',
                params: ({ context, event }) => ({
                  errorMessage: event.error instanceof Error ? event.error.message : undefined,
                  isHost: Boolean(context.cart.canSubmitOrder),
                }),
              },
            },
          },
        },
      },
    },
    lineItems: {
      initial: 'idle',
      states: {
        idle: {
          id: 'line-items-idle',
        },
        waiting: {
          id: 'line-items-waiting',
          after: {
            2000: {
              target: '#line-items-idle',
              actions: ['clearLineItemIdsBeingUpdated'],
            },
          },
        },
      },
    },
    wantedTimes: {
      initial: 'waiting',
      states: {
        unfocused: {
          id: 'wanted-times-unfocused',
          on: { START_WANTED_TIMES_POLLING: 'polling' },
        },
        polling: {
          id: 'wanted-times-polling',
          on: {
            STOP_WANTED_TIMES_POLLING: 'unfocused',
          },
          invoke: {
            id: 'fetchWantedTimes',
            src: 'fetchWantedTimes',
            onDone: {
              target: '#wanted-times-waiting',
              actions: ['updateWantedTimes'],
              guard: 'checkIfHasWantedTimes',
            },
          },
        },
        waiting: {
          id: 'wanted-times-waiting',
          on: {
            START_WANTED_TIMES_POLLING: 'polling',
            STOP_WANTED_TIMES_POLLING: 'unfocused',
          },
          after: {
            wantedTimesPolling: {
              target: 'polling',
            },
          },
        },
      },
    },
    bagCartPolling: {
      initial: 'waiting',
      states: {
        unfocused: {
          id: 'cart-polling-unfocused',
          on: {
            START_CART_POLLING: {
              target: 'polling',
              guard: 'checkIfBagCartPollingEnabled',
            },
          },
        },
        polling: {
          id: 'cart-polling',
          on: {
            STOP_CART_POLLING: 'unfocused',
          },
          invoke: {
            id: 'fetchBagCartPolling',
            src: 'fetchBagCartPolling',
            onDone: {
              target: '#cart-polling-waiting',
              actions: [
                {
                  type: 'updateCartFromPolling',
                  params: ({ event }) => ({ cart: event.output.cart }),
                },
              ],
            },
          },
        },
        waiting: {
          id: 'cart-polling-waiting',
          on: {
            START_CART_POLLING: {
              target: 'polling',
              guard: 'checkIfBagCartPollingEnabled',
            },
            STOP_CART_POLLING: 'unfocused',
          },
          after: {
            bagCartPolling: {
              target: 'polling',
              guard: 'checkIfBagCartPollingEnabled',
            },
          },
        },
      },
    },
    rewards: {
      initial: 'idle',
      states: {
        idle: {
          id: 'rewards-idle',
          on: {
            ASSIGN_REWARDS: {
              actions: ['updateRewards'],
            },
            REWARDS_FETCH_ERROR: {
              target: '#rewards-error',
              actions: ['clearRewards', 'onFetchRewardsError'],
            },
            REFETCH_REWARDS: [
              { target: '#fetching-rewards', guard: 'checkIfLoggedIn' },
              { target: '#rewards-idle' },
            ],
          },
        },
        error: {
          id: 'rewards-error',
          on: {
            REFETCH_REWARDS: [
              { target: '#fetching-rewards', guard: 'checkIfLoggedIn' },
              { target: '#rewards-idle' },
            ],
          },
        },
        preparingToFetchRewards: {
          id: 'preparing-to-fetch-rewards',
          always: [
            {
              target: '#fetching-rewards-deferred',
              guard: 'checkIfLoggedIn',
            },
            {
              target: '#rewards-idle',
            },
          ],
        },
        fetchingRewards: {
          id: 'fetching-rewards',
          invoke: {
            id: 'fetchRewards',
            src: 'fetchRewards',
            onDone: {
              target: '#rewards-idle',
              actions: ['updateRewards'],
            },
            onError: {
              target: '#rewards-error',
              actions: ['onFetchRewardsError'],
            },
          },
        },
        fetchingRewardsDeferred: {
          id: 'fetching-rewards-deferred',
          on: {
            ASSIGN_REWARDS: {
              target: '#rewards-idle',
              actions: ['updateRewards'],
            },
            REWARDS_FETCH_ERROR: {
              target: '#rewards-error',
              actions: ['clearRewards', 'onFetchRewardsError'],
            },
          },
        },
      },
    },
  },
});
